import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Building2,
  Users,
  Wrench,
  FileText,
  Shield,
  Phone,
  ChartBar,
  BarChart3,
  Clock,
  Home,
  Briefcase,
  HandCoins,
  Award,
  TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import StickyValuationCTA from "../components/StickyValuationCTA";
import api from "../lib/api";
import logger from "../lib/logger";

const ICONS = {
  Users,
  FileText,
  Building2,
  Wrench,
  Shield,
  ChartBar,
  BarChart3,
  Clock,
  Home,
  Briefcase,
  HandCoins,
  Award,
  TrendingUp,
};

const DEFAULTS = {
  hero_eyebrow: "Our Services",
  title: "Property Management",
  description:
    "Full hands-on property management for landlords across West Yorkshire. We handle everything from tenant find to day-to-day maintenance, so you can enjoy the returns without the hassle.",
  hero_cta_text: "Book a Free Consultation",
  services_section_title: "What's Included",
  services_section_subtitle:
    "Our comprehensive property management service covers every aspect of letting your property.",
  services_items: [
    { icon: "Users", title: "Tenant Find & Referencing", description: "Professional marketing, viewings, and comprehensive tenant screening including credit checks, employment verification, and landlord references." },
    { icon: "FileText", title: "Tenancy Setup", description: "Full tenancy agreement preparation, Right to Rent checks, deposit registration, and inventory creation." },
    { icon: "Building2", title: "Rent Collection", description: "Reliable rent collection with prompt payment to landlords. We handle arrears professionally and efficiently." },
    { icon: "Wrench", title: "Maintenance Coordination", description: "24/7 emergency response with a network of trusted West Yorkshire contractors for all repair needs." },
    { icon: "Shield", title: "Property Inspections", description: "Regular property inspections with detailed reports and photographs to protect your investment." },
    { icon: "ChartBar", title: "Compliance & Reporting", description: "We ensure your property meets all legal requirements including EPC, Gas Safety, EICR, and provide regular financial statements." },
  ],
  benefits_section_title: "Why Choose Keystone for Property Management",
  benefits_items: [
    "Competitive management fees",
    "No hidden charges or surprise costs",
    "Local West Yorkshire expertise",
    "Dedicated property manager",
    "Regular property inspections",
    "24/7 maintenance emergency line",
    "Full compliance management",
    "Transparent monthly statements",
  ],
  image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
  cta_title: "Ready to Hand Over the Management?",
  cta_description: "Get a free, no-obligation consultation and find out how we can help manage your property.",
  cta_button_text: "Book a Free Consultation",
  cta_phone: "01924 520163",
};

const PropertyManagementPage = () => {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("services_property_management")
      .then((res) => {
        if (mounted && res?.content) {
          setContent({ ...DEFAULTS, ...res.content });
        }
      })
      .catch((err) => {
        logger.warn("Failed to load services_property_management:", err?.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const renderIcon = (name, className) => {
    const Icon = ICONS[name] || Building2;
    return <Icon className={className} />;
  };

  const ctaPhoneDigits = (content.cta_phone || "").replace(/\s/g, "");

  return (
    <div data-testid="property-management-page">
      <StickyValuationCTA source="property-management-page" />
      {/* Hero */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              {content.hero_eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {content.title}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">{content.description}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold" data-testid="pm-cta-valuation">
                <Link to="/contact">
                  {content.hero_cta_text}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                data-testid="pm-cta-view-packages"
              >
                <Link to="/services/packages">
                  View Packages &amp; Pricing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.services_section_title}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{content.services_section_subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="pm-services-grid">
            {(content.services_items || []).map((service, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-lg bg-navy/5 flex items-center justify-center mb-6">
                    {renderIcon(service.icon, "w-7 h-7 text-navy")}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Callout */}
      <section className="py-12 md:py-16 bg-white" data-testid="pm-packages-callout">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-navy via-navy to-navy-light p-8 md:p-12">
            {/* Decorative gold corner */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-gold/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <span className="text-gold font-medium text-xs sm:text-sm tracking-wider uppercase mb-3 block">
                  Choose Your Service Level
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3 leading-tight">
                  Bronze, Silver, Gold &amp; Platinum Packages
                </h3>
                <p className="text-white/75 leading-relaxed">
                  Flexible management solutions from tenant-find only through to full HMO portfolio management. Transparent pricing, no hidden fees.
                </p>
              </div>
              <div className="md:text-right">
                <Button
                  asChild
                  size="lg"
                  className="bg-gold hover:bg-gold-light text-navy font-semibold w-full md:w-auto"
                  data-testid="pm-packages-callout-cta"
                >
                  <Link to="/services/packages">
                    View Packages
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-8">
                {content.benefits_section_title}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="pm-benefits-grid">
                {(content.benefits_items || []).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={content.image} alt={content.title} className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">{content.cta_title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{content.cta_description}</p>
          <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold">
            <Link to="/contact">
              {content.cta_button_text}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <div className="mt-6 flex items-center justify-center gap-2 text-white/70">
            <Phone className="w-5 h-5" />
            <span>Or call </span>
            <a href={`tel:${ctaPhoneDigits}`} className="text-gold hover:text-gold-light font-medium">
              {content.cta_phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagementPage;
